"use client";

import { useState } from "react";
import { FiEye } from "react-icons/fi";

import DocumentViewDialog from "@/components/patient/documents/document-view-dialog";
import Button from "@/components/ui/button";

import { getFileIcon } from "@/lib/file-utils";
import { formatDate, formatToHumanReadable } from "@/lib/format-data";

import { DocumentSummary } from "@/types/document";

interface SourceDocumentsProps {
  sourceDocuments: DocumentSummary[];
  className?: string;
}

export default function SourceDocuments({
  sourceDocuments,
  className = "",
}: SourceDocumentsProps) {
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentSummary | null>(null);
  const [documentViewDialogOpen, setDocumentViewDialogOpen] = useState(false);

  if (!sourceDocuments || sourceDocuments.length === 0) {
    return null;
  }

  const handleViewDocument = (document: DocumentSummary) => {
    setSelectedDocument(document);
    setDocumentViewDialogOpen(true);
  };

  return (
    <div className={className}>
      <h4 className="mb-4 text-body-strong text-foreground">
        Source Documents ({sourceDocuments.length})
      </h4>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sourceDocuments.map((document) => (
          <div
            key={document.id}
            className="group relative cursor-pointer rounded-lg border p-3 transition-shadow hover:shadow-sm"
            onClick={() => handleViewDocument(document)}
          >
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {getFileIcon(document.fileType)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-body-strong text-foreground">
                  {document.title}
                </p>
                <div className="mt-0.5 flex items-center gap-2 text-caption text-foreground-muted">
                  <span>{formatToHumanReadable(document.docType)}</span>
                  {document.createdDate && (
                    <>
                      <span>·</span>
                      <span>{formatDate(document.createdDate)}</span>
                    </>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDocument(document);
                }}
                className="h-7 w-7 flex-shrink-0 p-0 opacity-0 group-hover:opacity-100"
                tooltip="View Document"
              >
                <FiEye className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <DocumentViewDialog
        isOpen={documentViewDialogOpen}
        onOpenChange={setDocumentViewDialogOpen}
        document={selectedDocument}
      />
    </div>
  );
}
